import React, { Suspense, useContext, useEffect, useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PaletteMode, ThemeOptions, ThemeProvider, createTheme } from '@mui/material';

import UserContext from './shared/contexts/user-context';
import LoadingSpinner from './shared/components/UiElements/LoadingSpinner';
import styles from './App.module.css';

const Login = React.lazy(() => import('./users/pages/Login'));
const NavigationBar = React.lazy(() => import('./shared/components/Navigation/NavigationBar'));
const PasswordReset = React.lazy(() => import('./users/pages/PasswordReset'));
const NewPassword = React.lazy(() => import('./users/pages/NewPassword'));
const Profile = React.lazy(() => import('./users/pages/Profile'));
const Ranking = React.lazy(() => import('./rankings/pages/Ranking'));
const Rankings = React.lazy(() => import('./rankings/pages/Rankings'));
const User = React.lazy(() => import('./users/pages/User'));
const Users = React.lazy(() => import('./users/pages/Users'));
const NewUser = React.lazy(() => import('./users/pages/NewUser'));

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light' ?
      {
        primary: {
          main: '#2196f3',
          light: '#bbdefb',
          dark: '#0d47a1',
          contrastText: '#fff'
        }
      } :
      {
        primary: {
          main: '#2196f3',
          light: '#bbdefb',
          dark: '#0d47a1',
          contrastText: '#fff'
        }
      }
    )
  },
  typography: {
    allVariants: {
      fontFamily: [
        'sans-serif'
      ].join(',')
    },
    fontSize: 13
  }
});

const App: React.FC = () => {
  const defaultDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const { isLoggedIn, isAdmin, userSettings, toggleDarkMode } = useContext(UserContext);

  useEffect(() => {
    if (isLoggedIn && userSettings?.isDarkMode == null) {
      toggleDarkMode(defaultDark);
    }
  }, [isLoggedIn, userSettings?.isDarkMode, defaultDark, toggleDarkMode]);

  const theme = useMemo(() => createTheme(getDesignTokens(userSettings ? (userSettings.isDarkMode ? 'dark' : 'light') :
    (defaultDark ? 'dark' : 'light')))
  , [userSettings, defaultDark]);
  
  const routes =
    <Routes>
      <Route path='/' element={isLoggedIn ? <Rankings /> : <Ranking />} />
      <Route path='/login' element={isLoggedIn ? <Rankings /> : <Login />} />
      <Route path='/passwordReset' element={<PasswordReset />} />
      <Route path='/rankings' element={isLoggedIn ? <Rankings /> : <Login />} />
      <Route path='/rankings/new' element={<Ranking />} />
      <Route path='/rankings/:rankingId' element={<Ranking />} />
      <Route path='/reset/:token' element={<NewPassword />} />
      {isAdmin && <Route path='/users' element={isLoggedIn ? <Users /> : <Login />} />}
      <Route path='/users/profile' element={isLoggedIn ? <Profile /> : <Login />} />
      {isAdmin && <Route path='/users/new' element={isLoggedIn ? <NewUser /> : <Login />} />}
      {isAdmin && <Route path='/users/:userId' element={isLoggedIn ? <User /> : <Login />} />}
    </Routes>;

  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner message=''/>}>
          {isLoggedIn && <NavigationBar />}
          <main data-theme={userSettings ? (userSettings.isDarkMode ? 'dark' : 'light') : (defaultDark ? 'dark' : 'light')}
            className={`${styles['main-container']} ${isLoggedIn ? styles['adjusted-height'] : ''}`}>
            {routes}
          </main>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
